import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, BatteryCharging, Plug, Car, Play, Loader2, RotateCcw, Thermometer, AlertTriangle } from 'lucide-react';
import {
  REQUIRED_FIELD_SPECS, AMBIENT_TEMP_FIELD_SPEC, THERMAL_FIELD_SPECS, SOH_FIELD_SPEC,
  DC_CHARGER_POWER_FIELD_SPEC, DC_CHARGER_LIMIT_FIELD_SPECS, VEHICLE_MAX_DC_FIELD_SPEC, VEHICLE_OBC_FIELD_SPEC,
  CHEMISTRY_OPTIONS, CHARGER_KIND_OPTIONS, AC_POWER_OPTIONS,
  VEHICLE_PRESETS, VEHICLE_PRESET_BY_VALUE, DEFAULT_INPUTS,
  validateRequiredField, toRawStrings, typicalDcChargerLimits,
} from '../../lib/chargingSimulatorTools';
import { simulationApi } from '../../lib/simulationApi';
import ChargingSummaryPanel from './simulator/ChargingSummaryPanel';
import ChargingTimelinePanel from './simulator/ChargingTimelinePanel';
import ChargingCurveChart from './simulator/ChargingCurveChart';
import ChargingSessionChart from './simulator/ChargingSessionChart';
import ChargingThermalChart from './simulator/ChargingThermalChart';

const FieldInput = ({ spec, raw, error, onChange, testPrefix }) => (
  <div className="space-y-2">
    <Label htmlFor={`${testPrefix}-${spec.key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
      {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
    </Label>
    <Input
      id={`${testPrefix}-${spec.key}`}
      type="number"
      step="any"
      value={raw}
      onChange={(e) => onChange(spec.key, e.target.value)}
      className="font-mono"
      aria-invalid={!!error}
      aria-describedby={error ? `${testPrefix}-${spec.key}-error` : undefined}
      data-testid={`${testPrefix}-${spec.key}-input`}
    />
    {error && (
      <p id={`${testPrefix}-${spec.key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${testPrefix}-${spec.key}-error`}>
        <AlertCircle className="h-3 w-3 shrink-0" /> {error}
      </p>
    )}
  </div>
);

const ChargingSimulator = () => {
  const [presetValue, setPresetValue] = useState('');
  const [chemistry, setChemistry] = useState(DEFAULT_INPUTS.chemistry);
  const [chargerKind, setChargerKind] = useState(DEFAULT_INPUTS.charger_kind);
  const [raw, setRaw] = useState(() => toRawStrings(DEFAULT_INPUTS));
  // Starting Battery Temperature tracks Ambient Temperature by default (both start at the
  // same value) until the user directly edits it -- then it becomes independent, same
  // "auto-fill stays editable" convention as the vehicle presets below.
  const [batteryTempLinked, setBatteryTempLinked] = useState(true);
  // Same convention for the DC charger's max current/voltage: they track the typical limits
  // for the entered Charger Power (typicalDcChargerLimits) until the user edits either one.
  const [chargerLimitsLinked, setChargerLimitsLinked] = useState(true);

  const setField = (key, value) => {
    setRaw((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'ambient_temp_c' && batteryTempLinked) next.battery_temp_c = value;
      if (key === 'dc_charger_power_kw' && chargerLimitsLinked) {
        const limits = typicalDcChargerLimits(Number(value));
        if (limits) {
          next.dc_charger_max_current_a = String(limits.currentA);
          next.dc_charger_max_voltage_v = String(limits.voltageV);
        }
      }
      return next;
    });
    if (key === 'battery_temp_c') setBatteryTempLinked(false);
    if (key === 'dc_charger_max_current_a' || key === 'dc_charger_max_voltage_v') setChargerLimitsLinked(false);
  };

  // Selecting a preset re-seeds the VEHICLE's figures -- usable capacity, voltage, chemistry,
  // and its own charge ceilings (max DC power, onboard-charger rating). It deliberately leaves
  // the charger alone: the charger is whatever the user plugs into, and the vehicle's ceiling
  // then caps it (the backend reports when it does). Fields stay fully editable afterward.
  useEffect(() => {
    if (!presetValue) return;
    const preset = VEHICLE_PRESET_BY_VALUE[presetValue];
    if (!preset) return;
    setChemistry(preset.chemistry);
    setRaw((prev) => ({
      ...prev,
      capacity_kwh: String(preset.capacityKwh),
      nominal_voltage_v: String(preset.nominalVoltageV),
      vehicle_max_dc_kw: String(preset.dcMaxPowerKw),
      vehicle_obc_kw: String(preset.acMaxPowerKw),
    }));
  }, [presetValue]);

  const isDc = chargerKind === 'DC';
  const vehicleLimitSpec = isDc ? VEHICLE_MAX_DC_FIELD_SPEC : VEHICLE_OBC_FIELD_SPEC;

  const { errors, values } = useMemo(() => {
    const errs = {};
    const vals = {};
    const check = (spec) => {
      const err = validateRequiredField(spec, raw[spec.key]);
      if (err) errs[spec.key] = err;
      else if (raw[spec.key] !== '' && raw[spec.key] !== undefined) vals[spec.key] = Number(raw[spec.key]);
    };
    for (const spec of REQUIRED_FIELD_SPECS) check(spec);
    check(SOH_FIELD_SPEC);
    if (isDc) {
      check(DC_CHARGER_POWER_FIELD_SPEC);
      DC_CHARGER_LIMIT_FIELD_SPECS.forEach(check);
      vals.charger_power_kw = vals.dc_charger_power_kw;
    } else {
      vals.charger_power_kw = Number(raw.ac_charger_power_kw);
    }
    check(vehicleLimitSpec);
    vals.vehicle_max_charge_power_kw = vals[vehicleLimitSpec.key];
    for (const spec of [AMBIENT_TEMP_FIELD_SPEC, ...THERMAL_FIELD_SPECS]) check(spec);
    if (!errs.target_soc && !errs.start_soc && vals.target_soc !== undefined && vals.start_soc !== undefined
        && vals.target_soc <= vals.start_soc) {
      errs.target_soc = 'Must be greater than Start SoC.';
    }
    return { errors: errs, values: vals };
  }, [raw, isDc, vehicleLimitSpec]);

  // Input-time hint (the backend also reports this, with the result): a charger rated above
  // the vehicle's own ceiling can't deliver its full power.
  const vehicleCapHint = values.charger_power_kw && values.vehicle_max_charge_power_kw
    && values.charger_power_kw > values.vehicle_max_charge_power_kw
    ? `The vehicle will limit this session to ${values.vehicle_max_charge_power_kw} kW (charger rated ${values.charger_power_kw} kW).`
    : null;

  const hasErrors = Object.keys(errors).length > 0;

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [result, setResult] = useState(null);
  const [stale, setStale] = useState(false);
  const [submittedRange, setSubmittedRange] = useState(null);

  useEffect(() => {
    if (result) setStale(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, chemistry, chargerKind]);

  const runSimulation = async () => {
    setRunning(true);
    setRunError(null);
    try {
      const payload = {
        capacity_kwh: values.capacity_kwh,
        nominal_voltage_v: values.nominal_voltage_v,
        state_of_health_pct: values.state_of_health_pct ?? null,
        chemistry,
        charger_kind: chargerKind,
        charger_power_kw: values.charger_power_kw,
        vehicle_max_charge_power_kw: values.vehicle_max_charge_power_kw ?? null,
        dc_charger_max_current_a: isDc ? (values.dc_charger_max_current_a ?? null) : null,
        dc_charger_max_voltage_v: isDc ? (values.dc_charger_max_voltage_v ?? null) : null,
        start_soc: values.start_soc,
        target_soc: values.target_soc,
        ambient_temp_c: values.ambient_temp_c ?? null,
        battery_temp_c: values.battery_temp_c ?? null,
        cooling_power_kw: values.cooling_power_kw ?? null,
        cooling_cop: values.cooling_cop ?? null,
        heating_power_kw: values.heating_power_kw ?? null,
        heating_cop: values.heating_cop ?? null,
      };
      const response = await simulationApi.runCharging(payload);
      setResult(response);
      setSubmittedRange({ start: values.start_soc, target: values.target_soc });
      setStale(false);
    } catch (e) {
      setRunError(e.message);
    }
    setRunning(false);
  };

  return (
    <div className="space-y-6">
      {/* 1. Inputs */}
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <BatteryCharging className="h-5 w-5 text-autoxium-blue" />
            Charging Simulator
          </CardTitle>
          <CardDescription>
            Fill in battery, charger, and SoC range, then submit to run a realistic CC-CV
            charging simulation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Vehicle Preset</Label>
            <Select value={presetValue} onValueChange={setPresetValue}>
              <SelectTrigger className="sm:w-96" data-testid="charging-preset-select"><SelectValue placeholder="Custom (No Preset)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">Custom (No Preset)</SelectItem>
                {VEHICLE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Accordion type="multiple" defaultValue={['battery', 'charger', 'range', 'ambient', 'thermal']}>
            <AccordionItem value="battery">
              <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-2"><BatteryCharging className="h-4 w-4" /> Battery</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <FieldInput spec={REQUIRED_FIELD_SPECS[0]} raw={raw.capacity_kwh} error={errors.capacity_kwh} onChange={setField} testPrefix="charging" />
                  <FieldInput spec={REQUIRED_FIELD_SPECS[1]} raw={raw.nominal_voltage_v} error={errors.nominal_voltage_v} onChange={setField} testPrefix="charging" />
                  <FieldInput spec={SOH_FIELD_SPEC} raw={raw.state_of_health_pct} error={errors.state_of_health_pct} onChange={setField} testPrefix="charging" />
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Chemistry</Label>
                    <Select value={chemistry} onValueChange={setChemistry}>
                      <SelectTrigger data-testid="charging-chemistry-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CHEMISTRY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="charger">
              <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-2"><Plug className="h-4 w-4" /> Charger</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Charger Type</Label>
                    <Select value={chargerKind} onValueChange={setChargerKind}>
                      <SelectTrigger data-testid="charging-charger-kind-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CHARGER_KIND_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {!isDc ? (
                    <div className="space-y-2">
                      <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">AC Supply (EVSE) Power</Label>
                      <Select value={raw.ac_charger_power_kw} onValueChange={(v) => setField('ac_charger_power_kw', v)}>
                        <SelectTrigger data-testid="charging-ac-power-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {AC_POWER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <FieldInput spec={DC_CHARGER_POWER_FIELD_SPEC} raw={raw.dc_charger_power_kw} error={errors.dc_charger_power_kw} onChange={setField} testPrefix="charging" />
                  )}
                  {isDc && DC_CHARGER_LIMIT_FIELD_SPECS.map((spec) => (
                    <FieldInput key={spec.key} spec={spec} raw={raw[spec.key]} error={errors[spec.key]} onChange={setField} testPrefix="charging" />
                  ))}
                  <FieldInput spec={vehicleLimitSpec} raw={raw[vehicleLimitSpec.key]} error={errors[vehicleLimitSpec.key]} onChange={setField} testPrefix="charging" />
                </div>
                {vehicleCapHint && (
                  <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-3" data-testid="charging-vehicle-cap-hint">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> {vehicleCapHint}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-3">
                  {isDc
                    ? 'Charger Max Current/Voltage fill in typical values for the entered power until you edit them. '
                    : 'The AC supply is capped by the vehicle\'s onboard charger, whose conversion loss (~85-92%) reduces the power reaching the battery. '}
                  The cooling/heating system and 12 V load also run off the charger, so they reduce
                  charging power too. Leave the vehicle limit blank to cap only by the battery. See the user guide.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="range">
              <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-2"><Car className="h-4 w-4" /> SoC Range</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <FieldInput spec={REQUIRED_FIELD_SPECS[2]} raw={raw.start_soc} error={errors.start_soc} onChange={setField} testPrefix="charging" />
                  <FieldInput spec={REQUIRED_FIELD_SPECS[3]} raw={raw.target_soc} error={errors.target_soc} onChange={setField} testPrefix="charging" />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ambient">
              <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-2"><Thermometer className="h-4 w-4" /> Ambient Temperature</span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-xs text-muted-foreground mb-4">
                  The pack starts this session at this temperature (Starting Battery Temperature
                  below tracks this value until you edit it directly) — a cold or hot ambient
                  directly shapes the pack's internal resistance and current from the very first
                  row of results.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <FieldInput spec={AMBIENT_TEMP_FIELD_SPEC} raw={raw.ambient_temp_c} error={errors.ambient_temp_c} onChange={setField} testPrefix="charging" />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="thermal">
              <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-2"><Thermometer className="h-4 w-4" /> Thermal Management</span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-xs text-muted-foreground mb-4">
                  Starting pack temperature (defaults to Ambient Temperature above, editable
                  independently) and the cooling/heating system's power and efficiency (COP).
                  These directly drive the pack's DC internal resistance and therefore the
                  charging current and time throughout the session — not a cosmetic side panel.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {THERMAL_FIELD_SPECS.map((spec) => (
                    <FieldInput key={spec.key} spec={spec} raw={raw[spec.key]} error={errors[spec.key]} onChange={setField} testPrefix="charging" />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* 2. Submit */}
          <div className="pt-4">
            <Button onClick={runSimulation} disabled={hasErrors || running} className="w-full" data-testid="run-charging-simulation-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Submit &amp; Run Simulation
            </Button>
            {runError && (
              <div className="flex items-center gap-2 text-sm text-destructive mt-3" data-testid="charging-run-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {runError}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 3. Results */}
      {result && submittedRange && (
        <div className={`space-y-6 transition-opacity ${stale ? 'opacity-50 pointer-events-none' : ''}`} data-testid="charging-results">
          {stale && (
            <Alert data-testid="charging-stale-banner" className="pointer-events-auto">
              <RotateCcw className="h-4 w-4" />
              <AlertDescription>Inputs have changed — submit again to update these results.</AlertDescription>
            </Alert>
          )}

          {result.summary.target_was_capped && (
            <Alert data-testid="charging-target-capped-banner" className="pointer-events-auto">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                The BMS ends charging when the CV-phase current tapers to its cutoff, which this
                model reaches at <strong>{result.summary.full_charge_soc_pct.toFixed(1)}% SoC</strong>.
                A real car shows 100% at that point, so results below treat it as the requested{' '}
                {submittedRange.target}%.
              </AlertDescription>
            </Alert>
          )}
          {!result.summary.target_reached && (
            <Alert variant="destructive" data-testid="charging-target-not-reached-banner" className="pointer-events-auto">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Charging stopped at <strong>{result.summary.full_charge_soc_pct.toFixed(1)}% SoC</strong>, short
                of the requested {submittedRange.target}% — see the notes below for why.
              </AlertDescription>
            </Alert>
          )}
          {result.warnings && result.warnings.length > 0 && (
            <Alert data-testid="charging-warnings-banner" className="pointer-events-auto">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc pl-4 space-y-1">
                  {result.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <ChargingSummaryPanel summary={result.summary} />
          <ChargingSessionChart result={result} />
          <ChargingCurveChart result={result} startSoc={submittedRange.start} targetSoc={submittedRange.target} />
          <ChargingThermalChart result={result} startSoc={submittedRange.start} targetSoc={submittedRange.target} />
          <ChargingTimelinePanel timeline={result.timeline} />
        </div>
      )}

      {!result && (
        <Card className="technical-card">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="charging-empty-state">
            Fill in the inputs above and submit to see the charging timeline and charts.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ChargingSimulator;
